/**
 * Formats an ISO date string into a human-readable date and time string.
 *
 * @param iso             - ISO 8601 date string (with or without trailing 'Z')
 * @param targetTimeZone  - Optional IANA timezone (defaults to browser's local timezone)
 * @returns               Formatted string e.g. "Apr 6, 2026, 4:42 PM"
 */
export const formatDateAndTime = (iso: string, targetTimeZone?: string): string => {
  try {
    // Ensure proper UTC interpretation by appending 'Z' if not present
    const normalizedIso = iso.endsWith('Z') ? iso : `${iso}Z`;
    const timeZone = targetTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date(normalizedIso);

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', iso);
      return '';
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timeZone,
    });

    return formatter.format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return new Date(iso).toLocaleString();
  }
};

/**
 * Formats today's date as "06 Apr 2026" (en-GB short style).
 * Used by AppLayout header.
 */
export const formatTodayHeader = (): string => {
  return new Date()
    .toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
};
